import type { FastFeedDailyRow } from "@/api/types";
import { Panel } from "@/components/ui";
import { useChartColors } from "@/lib/theme";
import { useECharts } from "@/lib/use-echarts";
import type { EChartsOption } from "echarts";
import { useMemo } from "react";

function ChartInner({
  rows,
  cc,
}: { rows: FastFeedDailyRow[]; cc: ReturnType<typeof useChartColors> }) {
  const option = useMemo<EChartsOption>(() => {
    let cum = 0;
    const cumRevenue = rows.map((r) => {
      cum += r.revenue_usdg;
      return +cum.toFixed(4);
    });

    return {
      grid: { left: 52, right: 52, top: 32, bottom: 32 },
      legend: {
        data: ["Daily Revenue", "Cumulative"],
        textStyle: { color: cc.text, fontSize: 10 },
        top: 6,
        right: 8,
      },
      xAxis: {
        type: "category",
        data: rows.map((r) => r.date),
        axisLabel: { fontSize: 10, color: cc.text },
        axisLine: { lineStyle: { color: cc.grid } },
        axisTick: { show: false },
        boundaryGap: false,
      },
      yAxis: [
        {
          type: "value",
          axisLabel: { fontSize: 10, color: cc.text, formatter: (v: number) => `$${v.toFixed(0)}` },
          splitLine: { lineStyle: { color: cc.grid, type: "dashed" } },
        },
        {
          type: "value",
          axisLabel: { fontSize: 10, color: cc.text, formatter: (v: number) => `$${v.toFixed(0)}` },
          splitLine: { show: false },
        },
      ],
      tooltip: {
        trigger: "axis",
        formatter: (p: unknown) => {
          const params = p as Array<{ seriesName: string; name: string; value: number }>;
          return `<b>${params[0].name}</b><br/>${params.map((s) => `${s.seriesName}: $${(+s.value).toFixed(2)}`).join("<br/>")}`;
        },
      },
      series: [
        {
          name: "Daily Revenue",
          type: "line",
          yAxisIndex: 0,
          data: rows.map((r) => r.revenue_usdg),
          step: "end",
          symbol: rows.length <= 5 ? "circle" : "none",
          symbolSize: 6,
          lineStyle: { color: "#818cf8", width: 2 },
          itemStyle: { color: "#818cf8" },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(129,140,248,0.35)" },
                { offset: 1, color: "rgba(129,140,248,0)" },
              ],
            },
          },
        },
        {
          name: "Cumulative",
          type: "line",
          yAxisIndex: 1,
          data: cumRevenue,
          step: "end",
          symbol: rows.length <= 5 ? "circle" : "none",
          symbolSize: 5,
          lineStyle: { color: "#f59e0b", width: 1.5, type: "dashed" },
          itemStyle: { color: "#f59e0b" },
        },
      ],
    };
  }, [rows, cc]);

  const ref = useECharts(option, [option]);
  return <div ref={ref} style={{ height: 220 }} />;
}

interface Props {
  rows: FastFeedDailyRow[];
  loading?: boolean;
}

export function FastFeedRevenueChart({ rows, loading = false }: Props) {
  const cc = useChartColors();
  return (
    <Panel title="REVENUE TREND">
      {loading ? (
        <div className="skeleton" style={{ height: 220, borderRadius: "var(--radius-panel)" }} />
      ) : rows.length === 0 ? (
        <div
          style={{
            height: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--color-text-muted)",
            fontSize: 12,
          }}
        >
          No data in this range
        </div>
      ) : (
        <ChartInner rows={rows} cc={cc} />
      )}
    </Panel>
  );
}
